import * as React from 'react';
import {styled, useTheme, Theme, CSSObject} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, {AppBarProps as MuiAppBarProps} from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {useLocation, useNavigate} from "react-router-dom";
import {useState} from "react";
import Diversity3Icon from '@mui/icons-material/Diversity3';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DashboardIcon from '@mui/icons-material/Dashboard';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useAuth} from "./AuthProvider";
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import {checkPermission, isSuperadmin} from "../utils/permission";
import {useModuleSettings} from "../hooks/moduleSettings";
import {ModuleKey, moduleEnabled} from "../utils/moduleEnabled";
import {useImpersonationStatus} from "../hooks/impersonation";
import ImpersonationBanner from "./ImpersonationBanner";
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import EventIcon from '@mui/icons-material/Event';
import SettingsIcon from '@mui/icons-material/Settings';
import { PrivateSettingsDialog } from './PrivateSettingsDialog';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SearchIcon from '@mui/icons-material/Search';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNotifications, useMarkAllNotificationsRead, useMarkNotificationRead } from '../hooks/notifications';

const IMPERSONATION_BANNER_HEIGHT = 44;

interface LayoutProps {
    children: React.ReactNode;
}

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({theme, open}) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

export default function Layout({children}: LayoutProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [open, setOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
    const [overflowAnchorEl, setOverflowAnchorEl] = useState<null | HTMLElement>(null);
    const [searchValue, setSearchValue] = useState('');
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const { isAuthenticated, logout } = useAuth();
    const { data: notificationsData } = useNotifications({ limit: 12, enabled: isAuthenticated });
    const markNotificationRead = useMarkNotificationRead();
    const markAllNotificationsRead = useMarkAllNotificationsRead();

    const publicPaths = new Set<string>(['/', '/login', '/forgot-password', '/reset-password']);
    const isMobileFullscreenBlogPath = isMobile && (
        pathname === '/blog/new'
        || /^\/blog\/[^/]+$/.test(pathname)
        || /^\/blog\/[^/]+\/edit$/.test(pathname)
    );
    const showShell = isAuthenticated && !publicPaths.has(pathname) && !isMobileFullscreenBlogPath; // <-- nur wenn eingeloggt



    const handleLogout = async () => {
        await logout();
    };

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const isSelected = (route: string): boolean => pathname.toLowerCase() === route.toLowerCase();
    const notificationsOpen = Boolean(notificationsAnchorEl);

    const openNotificationTarget = async (id: number, targetUrl: string) => {
        await markNotificationRead.mutateAsync({ id });
        setNotificationsAnchorEl(null);
        if (targetUrl) {
            navigate(targetUrl);
        } else {
            navigate('/notifications');
        }
    };

    const submitSearch = () => {
        const query = searchValue.trim();
        navigate(query ? `/search?q=${encodeURIComponent(query)}` : '/search');
    };

    const canAccessGlobalMail = isSuperadmin() || !checkPermission(4);

    const { modules, isLoading: modulesLoading, isError: modulesError } = useModuleSettings({ enabled: isAuthenticated });
    const moduleOn = (key: ModuleKey): boolean =>
        moduleEnabled(modules, key, { isLoading: modulesLoading, isError: modulesError });

    const { status: impersonationStatus } = useImpersonationStatus({ enabled: isAuthenticated });
    const impersonating = Boolean(impersonationStatus?.active);
    const bannerOffset = impersonating ? IMPERSONATION_BANNER_HEIGHT : 0;

    return (
        <Box sx={{ display: 'flex', width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
            <CssBaseline/>
            {isAuthenticated && <ImpersonationBanner />}
            {showShell && (
                <>
                    <AppBar position="fixed" open={open} sx={{ top: `${bannerOffset}px` }}>
                        <Toolbar
                            variant="dense"
                            sx={{
                                minHeight: { xs: 56, sm: 64 },
                                px: { xs: 1, sm: 2 },
                            }}
                        >
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                onClick={handleDrawerOpen}
                                edge="start"
                                size="small"
                                sx={[
                                    {
                                        marginRight: { xs: 1.5, sm: 2.5 },
                                        p: 0.5,
                                    },
                                    open && {display: 'none'},
                                ]}
                            >
                                <MenuIcon/>
                            </IconButton>
                            <Box
                                component="img"
                                src={`${process.env.PUBLIC_URL}/icons/favicon-192.png`}
                                alt="PV! Teutonia-Alemannia"
                                sx={{ height: 36, width: 'auto', mr: 1.25, display: 'block', flexShrink: 0, borderRadius: '6px' }}
                            />
                            <Typography
                                variant="subtitle1"
                                noWrap
                                component="div"
                                sx={{
                                    flexGrow: 1,
                                    minWidth: 0,
                                    fontWeight: 700,
                                    letterSpacing: '0.01em',
                                    fontSize: { xs: '1rem', sm: '1.05rem' },
                                }}
                            >
                                {isMobile ? 'Teutonia-Alemannia' : 'PV! Teutonia-Alemannia'}
                            </Typography>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={{ xs: 0.25, sm: 0.5 }}
                                sx={{ flexShrink: 0 }}
                            >
                                {isMobile ? (
                                    <IconButton
                                        size="small"
                                        sx={{ p: 0.75 }}
                                        color="inherit"
                                        aria-label="suche"
                                        onClick={() => submitSearch()}
                                    >
                                        <SearchIcon/>
                                    </IconButton>
                                ) : (
                                    <TextField
                                        placeholder="Suche…"
                                        size="small"
                                        value={searchValue}
                                        onChange={(event) => setSearchValue(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                event.preventDefault();
                                                submitSearch();
                                            }
                                        }}
                                        sx={{
                                            minWidth: 260,
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.14)',
                                                color: '#ffffff',
                                                borderRadius: 999,
                                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.25)' },
                                                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.45)' },
                                                '&.Mui-focused fieldset': { borderColor: 'rgba(255, 255, 255, 0.7)' },
                                            },
                                            '& input::placeholder': { color: 'rgba(255, 255, 255, 0.8)', opacity: 1 },
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.85)' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                                <IconButton
                                    size="small"
                                    sx={{ p: 0.75 }}
                                    color="inherit"
                                    aria-label="benachrichtigungen"
                                    onClick={(event) => setNotificationsAnchorEl(event.currentTarget)}
                                >
                                    <Badge badgeContent={notificationsData.unread_count} color="error" max={99}>
                                        <NotificationsNoneIcon/>
                                    </Badge>
                                </IconButton>
                                {isMobile ? (
                                    <IconButton
                                        size="small"
                                        sx={{ p: 0.75 }}
                                        color="inherit"
                                        aria-label="weitere aktionen"
                                        onClick={(event) => setOverflowAnchorEl(event.currentTarget)}
                                    >
                                        <MoreVertIcon/>
                                    </IconButton>
                                ) : (
                                    <>
                                        <IconButton size="small" sx={{ p: 0.75 }} color="inherit" aria-label="einstellungen" onClick={() => setSettingsOpen(true)}>
                                            <SettingsIcon/>
                                        </IconButton>
                                        <IconButton size="small" sx={{ p: 0.75 }} color="inherit" aria-label="logout" onClick={handleLogout}>
                                            <LogoutIcon/>
                                        </IconButton>
                                    </>
                                )}
                            </Stack>
                        </Toolbar>
                    </AppBar>
                    {isMobile ? (
                        <MuiDrawer
                            variant="temporary"
                            open={open}
                            onClose={handleDrawerClose}
                            ModalProps={{
                                keepMounted: true, // verbessert die Performance auf mobilen Geräten
                            }}
                        >
                            <DrawerHeader>
                                <IconButton onClick={handleDrawerClose}>
                                    {theme.direction === 'rtl' ? <ChevronRightIcon/> : <ChevronLeftIcon/>}
                                </IconButton>
                            </DrawerHeader>
                            <Divider/>
                            <List>
                                <ListItemButton selected={isSelected('/dashboard')} onClick={() => {
                                    navigate('/dashboard');
                                    handleDrawerClose();
                                }}>
                                    <ListItemIcon>
                                        <DashboardIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Übersicht"/>
                                </ListItemButton>
                                <ListItemButton selected={isSelected('/accounts')} onClick={() => {
                                    navigate('/accounts');
                                    handleDrawerClose();
                                }}>
                                    <ListItemIcon>
                                        <Diversity3Icon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Bundesbrüder"/>
                                </ListItemButton>
                                {moduleOn('blog') && (
                                <ListItemButton
                                    selected={isSelected('/blog')}
                                    onClick={() => {
                                        navigate('/blog');
                                        handleDrawerClose();
                                    }}
                                >
                                    <ListItemIcon>
                                        <ArticleOutlinedIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Blog"/>
                                </ListItemButton>
                                )}
                                <ListItemButton selected={isSelected('/events')} onClick={() => {
                                    navigate('/events');
                                    handleDrawerClose();
                                }}>
                                    <ListItemIcon>
                                        <EventIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Veranstaltungen"/>
                                </ListItemButton>
                                <ListItemButton selected={isSelected('/bookings')} onClick={() => {
                                    navigate('/bookings');
                                    handleDrawerClose();
                                }}>
                                    <ListItemIcon>
                                        <CalendarMonthIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Buchungskalender"/>
                                </ListItemButton>
                                {moduleOn('meetings') && (
                                <ListItemButton selected={isSelected('/meeting')} onClick={() => {
                                    navigate('/meeting');
                                    handleDrawerClose();
                                }}>
                                    <ListItemIcon>
                                        <VideoCallIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Meeting"/>
                                </ListItemButton>
                                )}
                                <ListItemButton selected={isSelected('/myreservations')} onClick={() => {
                                    navigate('/myreservations');
                                    handleDrawerClose();
                                }}>
                                    <ListItemIcon>
                                        <HomeIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Reservierungen"/>
                                </ListItemButton>
                                {moduleOn('hutbook') && (
                                <ListItemButton selected={isSelected('/huttenbuch')} onClick={() => {
                                    navigate('/huttenbuch');
                                    handleDrawerClose();
                                }}>
                                    <ListItemIcon>
                                        <MenuBookIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Hüttenbuch"/>
                                </ListItemButton>
                                )}
                                {moduleOn('storage') && (
                                <ListItemButton selected={isSelected('/storage')} onClick={() => {
                                    navigate('/storage');
                                    handleDrawerClose();
                                }}>
                                    <ListItemIcon>
                                        <FolderOpenIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Datenablage"/>
                                </ListItemButton>
                                )}
                                {canAccessGlobalMail && (
                                    <ListItemButton selected={isSelected('/mail')} onClick={() => {
                                        navigate('/mail');
                                        handleDrawerClose();
                                    }}>
                                        <ListItemIcon>
                                            <MarkEmailUnreadIcon/>
                                        </ListItemIcon>
                                        <ListItemText primary="Rundmail"/>
                                    </ListItemButton>
                                )}
                                {moduleOn('info') && (
                                <ListItemButton
                                    selected={isSelected('/infos')}
                                    onClick={() => {
                                        navigate('/infos');
                                        handleDrawerClose();
                                    }}
                                >
                                    <ListItemIcon>
                                        <InfoOutlinedIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Infos"/>
                                </ListItemButton>
                                )}
                                {checkPermission(2) && (
                                    <>
                                        <Divider/>
                                        <ListItemButton selected={isSelected('/einstellungen')} onClick={() => {
                                            navigate('/einstellungen');
                                            handleDrawerClose();
                                        }}>
                                            <ListItemIcon>
                                                <SettingsIcon/>
                                            </ListItemIcon>
                                            <ListItemText primary="Einstellungen"/>
                                        </ListItemButton>
                                    </>
                                )}
                            </List>
                            <Divider/>
                            <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <Box
                                    component="img"
                                    src={`${process.env.PUBLIC_URL}/icons/favicon-192.png`}
                                    alt=""
                                    sx={{ height: 34, width: 'auto', flexShrink: 0, borderRadius: '6px' }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 700, lineHeight: 1.25, color: '#1f4d2b' }}>
                                        PV! Teutonia-Alemannia
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                        Seit 1905
                                    </Typography>
                                </Box>
                            </Box>
                        </MuiDrawer>
                    ) : (
                        <Drawer
                            variant="permanent"
                            open={open}
                            sx={{
                                '& .MuiDrawer-paper': {
                                    top: `${bannerOffset}px`,
                                    height: `calc(100% - ${bannerOffset}px)`,
                                },
                            }}
                        >
                            <DrawerHeader>
                                <IconButton onClick={handleDrawerClose}>
                                    {theme.direction === 'rtl' ? <ChevronRightIcon/> : <ChevronLeftIcon/>}
                                </IconButton>
                            </DrawerHeader>
                            <Divider/>
                            <List>
                                <ListItemButton selected={isSelected('/dashboard')} onClick={() => { navigate('/dashboard'); handleDrawerClose(); }}>
                                    <ListItemIcon>
                                        <DashboardIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Übersicht"/>
                                </ListItemButton>
                                <ListItemButton selected={isSelected('/accounts')} onClick={() => { navigate('/accounts'); handleDrawerClose(); }}>
                                    <ListItemIcon>
                                        <Diversity3Icon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Bundesbrüder"/>
                                </ListItemButton>
                                {moduleOn('blog') && (
                                <ListItemButton
                                    selected={isSelected('/blog')}
                                    onClick={() => { navigate('/blog'); handleDrawerClose(); }}
                                >
                                    <ListItemIcon>
                                        <ArticleOutlinedIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Blog"/>
                                </ListItemButton>
                                )}
                                <ListItemButton selected={isSelected('/events')} onClick={() => { navigate('/events'); handleDrawerClose(); }}>
                                    <ListItemIcon>
                                        <EventIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Veranstaltungen"/>
                                </ListItemButton>
                                <ListItemButton selected={isSelected('/bookings')} onClick={() => { navigate('/bookings'); handleDrawerClose(); }}>
                                    <ListItemIcon>
                                        <CalendarMonthIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Buchungskalender"/>
                                </ListItemButton>
                                {moduleOn('meetings') && (
                                <ListItemButton selected={isSelected('/meeting')} onClick={() => { navigate('/meeting'); handleDrawerClose(); }}>
                                    <ListItemIcon>
                                        <VideoCallIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Meeting"/>
                                </ListItemButton>
                                )}
                                <ListItemButton selected={isSelected('/myreservations')} onClick={() => { navigate('/myreservations'); handleDrawerClose(); }}>
                                    <ListItemIcon>
                                        <HomeIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Reservierungen"/>
                                </ListItemButton>
                                {moduleOn('hutbook') && (
                                <ListItemButton selected={isSelected('/huttenbuch')} onClick={() => { navigate('/huttenbuch'); handleDrawerClose(); }}>
                                    <ListItemIcon>
                                        <MenuBookIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Hüttenbuch"/>
                                </ListItemButton>
                                )}

                                {moduleOn('storage') && (
                                <ListItemButton selected={isSelected('/storage')} onClick={() => { navigate('/storage'); handleDrawerClose(); }}>
                                    <ListItemIcon>
                                        <FolderOpenIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Datenablage"/>
                                </ListItemButton>
                                )}
                                {canAccessGlobalMail && (
                                    <ListItemButton selected={isSelected('/mail')} onClick={() => { navigate('/mail'); handleDrawerClose(); }}>
                                        <ListItemIcon>
                                            <MarkEmailUnreadIcon/>
                                        </ListItemIcon>
                                        <ListItemText primary="Rundmail"/>
                                    </ListItemButton>
                                )}
                                {moduleOn('info') && (
                                <ListItemButton
                                    selected={isSelected('/infos')}
                                    onClick={() => { navigate('/infos'); handleDrawerClose(); }}
                                >
                                    <ListItemIcon>
                                        <InfoOutlinedIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Infos"/>
                                </ListItemButton>
                                )}
                                {checkPermission(2) && (
                                    <>
                                        <Divider/>
                                        <ListItemButton selected={isSelected('/einstellungen')} onClick={() => { navigate('/einstellungen'); handleDrawerClose(); }}>
                                            <ListItemIcon>
                                                <SettingsIcon/>
                                            </ListItemIcon>
                                            <ListItemText primary="Einstellungen"/>
                                        </ListItemButton>
                                    </>
                                )}
                            </List>
                            <Divider/>
                            {open && (
                                <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <Box
                                        component="img"
                                        src={`${process.env.PUBLIC_URL}/icons/favicon-192.png`}
                                        alt=""
                                        sx={{ height: 34, width: 'auto', flexShrink: 0, borderRadius: '6px' }}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 700, lineHeight: 1.25, color: '#1f4d2b' }}>
                                            PV! Teutonia-Alemannia
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                            Seit 1905
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Drawer>
                    )}
                    <PrivateSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
                    <Menu
                        anchorEl={overflowAnchorEl}
                        open={Boolean(overflowAnchorEl)}
                        onClose={() => setOverflowAnchorEl(null)}
                    >
                        <MenuItem onClick={() => { setOverflowAnchorEl(null); setSettingsOpen(true); }}>
                            <ListItemIcon><SettingsIcon fontSize="small"/></ListItemIcon>
                            <ListItemText>Einstellungen</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => { setOverflowAnchorEl(null); void handleLogout(); }}>
                            <ListItemIcon><LogoutIcon fontSize="small"/></ListItemIcon>
                            <ListItemText>Abmelden</ListItemText>
                        </MenuItem>
                    </Menu>
                    <Menu
                        anchorEl={notificationsAnchorEl}
                        open={notificationsOpen}
                        onClose={() => setNotificationsAnchorEl(null)}
                        PaperProps={{ sx: { width: 360, maxWidth: 'calc(100vw - 24px)' } }}
                    >
                        <MenuItem
                            onClick={() => {
                                setNotificationsAnchorEl(null);
                                navigate('/notifications');
                            }}
                            sx={{ fontWeight: 700 }}
                        >
                            Alle Benachrichtigungen
                        </MenuItem>
                        {notificationsData.items.length === 0 && (
                            <MenuItem disabled>Keine Benachrichtigungen</MenuItem>
                        )}
                        {notificationsData.items.map((notification) => (
                            <MenuItem
                                key={notification.id}
                                onClick={() => { void openNotificationTarget(notification.id, notification.target_url); }}
                                sx={{
                                    alignItems: 'flex-start',
                                    whiteSpace: 'normal',
                                    backgroundColor: notification.is_read ? 'transparent' : '#f7fbf4',
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: notification.is_read ? 500 : 700 }}>
                                        {notification.title}
                                    </Typography>
                                    {notification.message && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                            {notification.message}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                        {new Date(notification.created_at).toLocaleString('de-DE')}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                        {notificationsData.unread_count > 0 && (
                            <MenuItem
                                onClick={() => {
                                    void markAllNotificationsRead.mutateAsync();
                                    setNotificationsAnchorEl(null);
                                }}
                            >
                                Alle als gelesen markieren
                            </MenuItem>
                        )}
                    </Menu>
                </>
            )}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    overflowX: 'hidden',
                    p: { xs: 1, sm: 2, md: 3 },
                    backgroundColor: 'background.default',
                    minHeight: '100vh',
                    transition: 'background-color 200ms ease',
                }}
            >
                {impersonating && <Box sx={{ height: `${IMPERSONATION_BANNER_HEIGHT}px` }} />}
                {showShell && <Toolbar/>}
                {children}
            </Box>

        </Box>
    );
}
